import { QUERY_SALES_CAMPAIGN_BY_ID } from '@/modules/campanhas/gestao-ofertas-v2/graphql'
import { MUTATION_STATICALLY_CLONE_SEGMENTS } from '@/modules/campanhas/views/components/graphql'
import { mergeAllIndividualAndGroupProductsByOffer, mergeAllComboProducts } from '@/modules/campanhas/gestao-ofertas-v2/helpers/products.js'
import { assertArray } from '@/config/helpers.js'
import { DateTime } from 'luxon'

export default {
  provide() {
    return {
      linkOfferScheduleToCampaign: this.linkOfferScheduleToCampaign,
      unlinkOfferScheduleInCampaign: this.unlinkOfferScheduleInCampaign,
    }
  },
  beforeRouteEnter({ params }, from, next) {
    next((vm) => {
      if (params?.offerScheduleId) {
        vm.linkOfferScheduleToCampaign(params.offerScheduleId)
      }
    })
  },
  data: () => ({
    offerSchedule: {},
    offerScheduleLoading: false,
    staticSegmentsLoading: false,
  }),
  computed: {
    offerScheduleAndStaticSegmentsLoading() {
      return this.offerScheduleLoading || this.staticSegmentsLoading
    },
    isWhatsappCampaign() {
      const isWhatsappCampaign = this.$route?.name === 'campanhas-whatsapp-cadastro' || this.$route?.name === 'campanhas-whatsapp-editar'
      return isWhatsappCampaign
    },
    isBannerCampaign() {
      const isBannerCampaign = this.$route?.name === 'banners-new'
      return isBannerCampaign
    },
    isMariaCampaign() {
      return this.$route?.name === 'new-maria-campaign'
    },
    isOfferSchedulePermissionActive() {
      return this.$getAllowedModules()['offer_management']
    },
    isOfferSchedulePersonalized() {
      return !!this.offerSchedule?.segments?.length
    },
  },
  methods: {
    getAndMergeAllProductsFromOffers(offerSchedule, groupKeyName) {
      const offerSalesProducts = mergeAllIndividualAndGroupProductsByOffer(offerSchedule.offer_sales, groupKeyName ?? 'offer_sales')
      const percentageOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(
        offerSchedule.percentage_offer_sales,
        groupKeyName ?? 'percentage_offer_sales'
      )
      const dynamicPricingPercentageOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(
        offerSchedule.dynamic_pricing_percentage_offer_sales,
        groupKeyName ?? 'dynamic_pricing_percentage_offer_sales'
      )
      const takeAndPayOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(offerSchedule.takepay_offer_sales, groupKeyName ?? 'takepay_offer_sales')
      const cashbackPercentageOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(
        offerSchedule.cashback_percentage_offer_sales,
        groupKeyName ?? 'cashback_percentage_offer_sales'
      )
      const cashbackAbsoluteOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(
        offerSchedule.cashback_absolute_offer_sales,
        groupKeyName ?? 'cashback_absolute_offer_sales'
      )
      const pointsOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(offerSchedule.points_offer_sales, groupKeyName ?? 'points_offer_sales')

      const comboOfferSalesProducts = assertArray(offerSchedule.combo_offer_sales).reduce((productList, product) => {
        productList.push(...mergeAllComboProducts(product.target_products, 'target_products'))
        productList.push(...mergeAllComboProducts(product.trigger_products, 'trigger_products'))

        return productList
      }, [])

      return [
        ...offerSalesProducts,
        ...percentageOfferSalesProducts,
        ...dynamicPricingPercentageOfferSalesProducts,
        ...takeAndPayOfferSalesProducts,
        ...comboOfferSalesProducts,
        ...cashbackPercentageOfferSalesProducts,
        ...cashbackAbsoluteOfferSalesProducts,
        ...pointsOfferSalesProducts,
      ]
    },
    async linkOfferScheduleToCampaign(id) {
      if (!this.isOfferSchedulePermissionActive) return
      if (Object.entries(this.offerSchedule) && !this.isBannerCampaign) this.unlinkOfferScheduleInCampaign()

      try {
        this.offerScheduleLoading = true
        const {
          data: { offerSchedule },
        } = await this.$apollo.query({
          query: QUERY_SALES_CAMPAIGN_BY_ID,
          variables: { id },
          context: {
            uri: this.$microservicesUrls['sales'],
          },
          fetchPolicy: 'no-cache',
        })

        if (offerSchedule?.segments?.length) {
          this.getStaticSegmentsAndAddToOfferSchedule(offerSchedule)
        } else {
          this.setCampaignWithDataFromOfferSales(offerSchedule)
        }
      } catch (error) {
        console.error(error)
        this.$snackbar({ message: 'Erro ao buscar dados de uma oferta', snackbarColor: 'error' })
      } finally {
        this.offerScheduleLoading = false
      }
    },
    async getStaticSegmentsAndAddToOfferSchedule(offerSchedule) {
      try {
        this.staticSegmentsLoading = true
        const campaignObjectName = this.getCampaignObjectName()
        const segments_ids = offerSchedule?.segments.map((segment) => segment._id)
        const variables = {
          segments_ids,
          name: this[campaignObjectName].title || offerSchedule.name,
          description: this[campaignObjectName].description || offerSchedule.description,
        }
        const {
          data: { staticallyCloneSegments },
        } = await this.$apollo.mutate({
          mutation: MUTATION_STATICALLY_CLONE_SEGMENTS,
          variables,
          fetchPolicy: 'no-cache',
        })

        this.setCampaignWithDataFromOfferSales(offerSchedule, staticallyCloneSegments)
      } catch {
        this.$snackbar({ message: 'Erro ao buscar segmentos', snackbarColor: 'error' })
      } finally {
        this.staticSegmentsLoading = false
      }
    },
    getCampaignObjectName() {
      const campaignObjectName = this.isWhatsappCampaign ? 'campanha' : 'campaign'
      return campaignObjectName
    },
    setCampaignWithDataFromOfferSales(offerSchedule, staticSegments = {}) {
      const campaignObjectName = this.getCampaignObjectName()
      const dateNow = DateTime.now().toFormat('yyyy-MM-dd')
      if (this.isMariaCampaign) {
        this.campaign.title = this.campaign.title || offerSchedule.name
        this.campaign.description = this.campaign.description || offerSchedule.description
        this.campaign.offerSelected = offerSchedule._id
      } else {
        if (this.isBannerCampaign) {
          this[campaignObjectName].action = {
            type: 'VIEW_SALES',
            reference_id: offerSchedule._id,
          }
        } else {
          this[campaignObjectName].products = this.getAndMergeAllProductsFromOffers(offerSchedule)
          this[campaignObjectName].offers_schedule_id = offerSchedule._id
          this[campaignObjectName].units = offerSchedule.units.map((unit) => unit.id_erp)
        }
        this[campaignObjectName].title = this[campaignObjectName].title || offerSchedule.name
        this[campaignObjectName].description = this[campaignObjectName].description || offerSchedule.description
        this[campaignObjectName].final_date = offerSchedule.end_date

        if (offerSchedule.start_date >= dateNow) {
          this[campaignObjectName].initial_date = offerSchedule.start_date
          if (this[campaignObjectName].message) {
            this[campaignObjectName].message.send_date = offerSchedule.start_date
          }
        } else {
          this[campaignObjectName].initial_date = dateNow
          if (this[campaignObjectName].message) {
            this[campaignObjectName].message.send_date = dateNow
          }
        }

        if (Object.entries(staticSegments).length) {
          if (this.isWhatsappCampaign) {
            this.chosenSegments = [staticSegments]
            this.targetingMode = 'SEGMENTED'
          } else {
            this[campaignObjectName].segments = [staticSegments]
          }
        }
      }
      this.offerSchedule = offerSchedule
    },
    unlinkOfferScheduleInCampaign() {
      const campaignObjectName = this.getCampaignObjectName()
      this[campaignObjectName].offers_schedule_id = ''
      this.offerSchedule = {}
      this[campaignObjectName].products = []

      if (this.setHighlightKeyValue) {
        this[campaignObjectName].productsGroups = []
        this.setHighlightKeyValue({ key: 'products', payload: [] })
        this.setHighlightKeyValue({ key: 'productsGroups', payload: [] })
      }

      if (this.isWhatsappCampaign && this?.chosenSegments?.length) {
        this.chosenSegments = []
        this.targetingMode = 'ALL'
        this.$forceUpdate()
      } else if (this[campaignObjectName]?.segments?.length) {
        this[campaignObjectName].segments = []
      }
      if (this[campaignObjectName]?.units?.length) {
        this[campaignObjectName].units = []
      }
    },
  },
}
